import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { StatusPill } from '../../components/ui/StatusPill.js';
import { SecretField } from '../../components/ui/SecretField.js';
import { useToast } from '../../components/ui/Toast.js';
import './credentials.css';

const CREATABLE_KINDS = ['do_token', 'cloudflare_token', 'ssh_key', 'spaces_key', 'github_token', 'github_app'] as const;
type CreatableKind = (typeof CREATABLE_KINDS)[number];

const KIND_LABELS: Record<CreatableKind, string> = {
  do_token: 'DigitalOcean token',
  cloudflare_token: 'Cloudflare token',
  ssh_key: 'SSH key',
  spaces_key: 'Spaces key',
  github_token: 'GitHub token',
  github_app: 'GitHub App',
};

interface Credential {
  id: string;
  name: string;
  kind: string;
  last4: string;
  fingerprint: string;
  isActive: boolean;
  rotatedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  ownerUserId: string | null;
  ownerUsername: string | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function CredentialsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: creds, isLoading } = useQuery<Credential[]>({
    queryKey: ['credentials'],
    queryFn: () => apiFetch<Credential[]>('/credentials'),
  });

  // ── add ──
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CreatableKind>('do_token');
  const [secret, setSecret] = useState('');
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  const [appId, setAppId] = useState('');
  const [installationId, setInstallationId] = useState('');

  const { data: users } = useQuery<Array<{ id: string; username: string }>>({
    queryKey: ['users'],
    queryFn: () => apiFetch('/users'),
    enabled: addOpen,
  });

  const resetAddForm = () => {
    setName('');
    setKind('do_token');
    setSecret('');
    setOwnerUserId(null);
    setAppId('');
    setInstallationId('');
  };

  const createMut = useMutation({
    mutationFn: (body: { name: string; kind: CreatableKind; secret: string; ownerUserId: string | null; appId?: string; installationId?: string }) =>
      apiFetch<Credential>('/credentials', { method: 'POST', body }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['credentials'] });
      setAddOpen(false);
      resetAddForm();
      toast.success(`Credential "${created.name}" added`);
    },
    onError: (err) => toast.error(errMessage(err, 'failed to add credential')),
  });

  // ── verify ──
  const verifyMut = useMutation({
    mutationFn: (cred: Credential) =>
      apiFetch<{ ok: boolean; detail: string }>(`/credentials/${cred.id}/verify`, {
        method: 'POST',
      }),
    onSuccess: (res, cred) => {
      if (res.ok) toast.success(`${cred.name}: ${res.detail || 'verified'}`);
      else toast.error(`${cred.name}: ${res.detail || 'verification failed'}`);
    },
    onError: (err) => toast.error(errMessage(err, 'verification failed')),
  });

  // ── patch (rotate / enable / disable) ──
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { secret?: string; isActive?: boolean } }) =>
      apiFetch<Credential>(`/credentials/${id}`, { method: 'PATCH', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err) => toast.error(errMessage(err, 'update failed')),
  });

  // ── rotate dialog ──
  const [rotateTarget, setRotateTarget] = useState<Credential | null>(null);
  const [rotateSecret, setRotateSecret] = useState('');

  const submitRotate = () => {
    if (!rotateTarget || rotateSecret.length < 8) return;
    patchMut.mutate(
      { id: rotateTarget.id, body: { secret: rotateSecret } },
      {
        onSuccess: () => {
          toast.success(`Secret rotated for "${rotateTarget.name}"`);
          setRotateTarget(null);
          setRotateSecret('');
        },
      },
    );
  };

  // ── delete ──
  const [deleteTarget, setDeleteTarget] = useState<Credential | null>(null);
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/credentials/${id}`, { method: 'DELETE' }),
    onSuccess: async (_res, id) => {
      await queryClient.invalidateQueries({ queryKey: ['credentials'] });
      const removed = creds?.find((c) => c.id === id);
      toast.success(`Credential "${removed?.name ?? 'credential'}" deleted`);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMessage(err, 'delete failed')),
  });

  const toggleActive = (cred: Credential) => {
    patchMut.mutate(
      { id: cred.id, body: { isActive: !cred.isActive } },
      {
        onSuccess: () =>
          toast.success(`"${cred.name}" ${cred.isActive ? 'disabled' : 'enabled'}`),
      },
    );
  };

  const columns: Column<Credential>[] = [
    { key: 'name', header: 'Name', render: (c) => <span className="cred-name">{c.name}</span> },
    { key: 'kind', header: 'Kind', render: (c) => <span className="badge">{c.kind}</span> },
    {
      key: 'scope',
      header: 'Scope',
      render: (c) => <span className="badge">{c.ownerUsername ? '@' + c.ownerUsername : 'Global'}</span>,
    },
    {
      key: 'last4',
      header: 'Secret',
      render: (c) => <span className="mono ui-cell-mute">…{c.last4}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) =>
        c.isActive ? (
          <StatusPill variant="ok">active</StatusPill>
        ) : (
          <StatusPill variant="err">disabled</StatusPill>
        ),
    },
    {
      key: 'rotatedAt',
      header: 'Rotated',
      render: (c) => <span className="ui-cell-mute">{fmtDate(c.rotatedAt)}</span>,
    },
    {
      key: 'lastUsedAt',
      header: 'Last used',
      render: (c) => <span className="ui-cell-mute">{fmtDate(c.lastUsedAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (c) => (
        <div className="ui-cell-actions">
          <button
            className="btn btn-ghost btn-sm"
            disabled={verifyMut.isPending && verifyMut.variables?.id === c.id}
            onClick={() => verifyMut.mutate(c)}
          >
            {verifyMut.isPending && verifyMut.variables?.id === c.id ? 'Verifying…' : 'Verify'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setRotateSecret('');
              setRotateTarget(c);
            }}
          >
            Rotate
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(c)}>
            {c.isActive ? 'Disable' : 'Enable'}
          </button>
          <button className="btn btn-ghost-danger btn-sm" onClick={() => setDeleteTarget(c)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  const canCreate = name.trim().length > 0 && secret.length >= 8;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Credentials</h1>
          <p className="page-sub">
            Provider tokens and keys. Secrets are write-only — only the last four characters are
            ever shown.
          </p>
        </div>
        <button className="btn btn-amber" onClick={() => setAddOpen(true)}>
          Add credential
        </button>
      </div>

      <Table
        columns={columns}
        rows={creds ?? []}
        rowKey={(c) => c.id}
        empty={isLoading ? 'Loading…' : 'No credentials yet. Add a provider token to get started.'}
      />

      {/* add dialog */}
      <Dialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          resetAddForm();
        }}
        title="Add credential"
      >
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCreate && !createMut.isPending) {
              createMut.mutate({ name: name.trim(), kind, secret, ownerUserId, ...(kind === 'github_app' ? { appId, installationId } : {}) });
            }
          }}
        >
          <div className="field">
            <label htmlFor="cred-name">Name</label>
            <input
              id="cred-name"
              autoFocus
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="prod DigitalOcean"
            />
          </div>
          <div className="field">
            <label htmlFor="cred-kind">Kind</label>
            <select
              id="cred-kind"
              className="mono"
              value={kind}
              onChange={(e) => setKind(e.target.value as CreatableKind)}
            >
              {CREATABLE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]} ({k})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cred-owner">Scope</label>
            <select
              id="cred-owner"
              className="mono"
              value={ownerUserId ?? ''}
              onChange={(e) => setOwnerUserId(e.target.value || null)}
            >
              <option value="">Global (any user)</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>@{u.username}</option>
              ))}
            </select>
          </div>
          {kind === 'github_app' && (
            <>
              <div className="field">
                <label htmlFor="cred-app-id">App ID</label>
                <input
                  id="cred-app-id"
                  className="mono"
                  maxLength={40}
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="123456"
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label htmlFor="cred-installation-id">Installation ID</label>
                <input
                  id="cred-installation-id"
                  className="mono"
                  maxLength={40}
                  value={installationId}
                  onChange={(e) => setInstallationId(e.target.value)}
                  placeholder="456789"
                  autoComplete="off"
                />
              </div>
            </>
          )}
          {kind === 'ssh_key' || kind === 'github_app' ? (
            <div className="field">
              <label htmlFor="cred-secret-key">
                {kind === 'github_app' ? 'Private key (PEM)' : 'Private key'}
              </label>
              <textarea
                id="cred-secret-key"
                className="mono secret-textarea"
                rows={7}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={kind === 'github_app' ? '-----BEGIN RSA PRIVATE KEY-----' : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          ) : (
            <div className="field">
              <label htmlFor="cred-secret">Secret</label>
              <input
                id="cred-secret"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          )}
          <div className="dialog-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setAddOpen(false);
                resetAddForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-amber" disabled={!canCreate || createMut.isPending}>
              {createMut.isPending ? 'Adding…' : 'Add credential'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* rotate dialog */}
      <Dialog
        open={rotateTarget !== null}
        onClose={() => setRotateTarget(null)}
        title={rotateTarget ? `Rotate "${rotateTarget.name}"` : 'Rotate secret'}
      >
        {rotateTarget ? (
          <form
            className="dialog-form"
            onSubmit={(e) => {
              e.preventDefault();
              submitRotate();
            }}
          >
            <p className="dialog-desc">
              Provide the new secret for this <span className="mono">{rotateTarget.kind}</span>{' '}
              credential. The old secret is replaced immediately.
            </p>
            <SecretField
              key={rotateTarget.id}
              id="rotate-secret"
              label="Secret"
              last4={rotateTarget.last4}
              value={rotateSecret}
              onChange={setRotateSecret}
              multiline={rotateTarget.kind === 'ssh_key'}
            />
            <div className="dialog-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setRotateTarget(null)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-amber"
                disabled={rotateSecret.length < 8 || patchMut.isPending}
              >
                {patchMut.isPending ? 'Rotating…' : 'Rotate secret'}
              </button>
            </div>
          </form>
        ) : null}
      </Dialog>

      {/* delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
        title="Delete credential"
        targetName={deleteTarget?.name ?? ''}
        description="Stacks and deployments that reference this credential will stop working."
        confirmLabel="Delete credential"
        busy={deleteMut.isPending}
      />
    </div>
  );
}
